import { CommonModule } from '@angular/common';
import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { MatLegacySnackBar as MatSnackBar } from '@angular/material/legacy-snack-bar';
import { AgGridModule } from 'ag-grid-angular';
import {
  ColDef,
  GridApi,
  GridOptions,
  GridReadyEvent,
  IServerSideDatasource,
  IServerSideGetRowsParams,
} from 'ag-grid-community';
import { BehaviorSubject, finalize, forkJoin, Subject, takeUntil } from 'rxjs';

import { SnackBarConfig } from '../../../../../shared/configs/snackbar.config';
import { LocalStorageKeys } from '../../../../../shared/interfaces/localstorage.interface';
import { FulfillmentOrdersGridConfig } from '../../configs/fulfillment-orders-grid.config';

// Constants
import {
  fulfillmentOrderDetailRowRelations,
  FulfillmentOrdersGridConstants,
} from '../../constants/fulfillment-orders-grid.constants';

// Services
import { FulfillmentOrdersGridActionService } from '../../services/fulfillment-orders-grid-action.service';
import { FulfillmentOrderApiService } from 'v2/modules/shared/services/api/fulfillment-order-api.service';
import { FeatureToggleService } from '@app/shared/services/feature-toggle.service';
import { InventoryService } from '@app/core/inventory/services/inventory.service';

// Types
import {
  EFulfillmentOrdersGridField,
  IFulfillmentOrdersGridColumnDefContext,
  IFulfillmentOrdersGridOptionsContext,
} from '../../types/fulfillment-orders-grid.types';

// Utils
import { AgGridUtils } from 'v2/modules/shared/util/ag-grid.utils';
import { AgGridStateUtils } from 'v2/modules/shared/util/ag-grid-state.utils';

@Component({
  selector: 'lum-fulfillment-orders-ag-table',
  standalone: true,
  imports: [CommonModule, AgGridModule],
  templateUrl: './fulfillment-orders-ag-table.component.html',
  styleUrls: ['./fulfillment-orders-ag-table.component.scss'],
})
export class FulfillmentOrdersAgTableComponent implements OnInit {
  @Output() selectedRowsChange = new EventEmitter<number[]>();

  @Input() height :string = '100%';
  @Input() width :string = '100%';
  @Input() salesOrderId :number;
  @Input() clearGridTrigger :EventEmitter<void>;
  @Input() refetchGridDataTrigger :EventEmitter<void>;

  columnDefs :ColDef[];
  gridOptions :GridOptions;
  selectedRowIds$ = new BehaviorSubject<number[]>([]);
  selectedRowCount$ = new BehaviorSubject<number>(0);
  fulfillmentConfirmationValues$ = new BehaviorSubject<any[]>([]);

  @Output() selectedRowCountEvent = new EventEmitter<number>();
  @Output() selectedRowIdsEvent = new EventEmitter<number[]>();
  @Output() selectedfulfillmentConfirmationValuesEvent = new EventEmitter<any[]>();
  @Output() selectedRowsEvent = new EventEmitter<any[]>();

  private gridApi :GridApi;
  private onDestroy$ = new Subject();

  constructor(
    private fulfillmentOrdersApiService :FulfillmentOrderApiService,
    private fulfillmentOrdersGridActionService :FulfillmentOrdersGridActionService,
    private snackBar :MatSnackBar,
    private featureToggleService :FeatureToggleService,
    private inventoryService :InventoryService,
  ) {}

  ngOnInit() :void {
    this.initColumnDefs();
    this.initGridOptions();
    this.initClearGrid();
    this.initRefetchGridData();
    this.initiateFulfillmentOrdersGridActionServiceClearGridState();
  }

  onGridReady(params :GridReadyEvent) :void {
    this.fulfillmentOrdersGridActionService.setGridApi(params.api);
    this.gridApi = params.api;
    this.gridApi.setServerSideDatasource(this.getServerSideDatasource());
    this.initDetailRowData();
    AgGridStateUtils.loadAgGridState(
      this.gridOptions,
      this.columnDefs,
      LocalStorageKeys.FULFILLMENT_ORDERS_AG_STATE,
    );
  }

  getServerSideDatasource() :IServerSideDatasource {
    return {
      getRows: (params :IServerSideGetRowsParams) => {
        this.gridOptions.api?.showLoadingOverlay();
        let requestParams = AgGridUtils.mapAgGridRequestParams(
          params,
          FulfillmentOrdersGridConstants,
          EFulfillmentOrdersGridField.FULFILLMENT_ORDER_KEY,
          this.gridOptions.paginationPageSize,
        );

        if (this.salesOrderId) {
          requestParams = AgGridUtils.addFiltersToRequestParams(requestParams, [
            {
              column: 'salesOrderId',
              operator: 'eq',
              value: this.salesOrderId.toString(),
            },
          ]);
        }

        this.fulfillmentOrdersApiService
          .get(requestParams)
          .pipe(
            takeUntil(this.onDestroy$),
            finalize(() => {
              this.gridOptions.api?.hideOverlay();
            }),
          )
          .subscribe({
            next: (res) => {
              params.successCallback(res.data, res.meta.total);
            },
            error: () => {
              params.failCallback();
            },
          });
      },
    };
  }

  clearGridState() :void {
    localStorage.removeItem(LocalStorageKeys.FULFILLMENT_ORDERS_AG_STATE);

    if (this.gridApi) {
      this.gridApi.setFilterModel(null);

      if (this.gridOptions.columnApi) {
        this.gridOptions.columnApi.applyColumnState({
          state: [],
          applyOrder: true,
        });

        this.gridOptions.columnApi.resetColumnState();
      }

      this.gridApi.refreshCells({ force: true });

      this.refetchGridData();
    }
  }

  private refetchGridData() :void {
    const datasource = this.getServerSideDatasource();
    this.gridApi.setServerSideDatasource(datasource);
    // Deselect all rows
    this.gridApi.deselectAll();
    // Clear selected row-related observables and emitters
    this.selectedRowCount$.next(0);
    this.selectedRowIds$.next([]);
    this.selectedRowCountEvent.emit(0);
    this.selectedRowIdsEvent.emit([]);
    this.selectedfulfillmentConfirmationValuesEvent.emit([]);
  }

  private initiateFulfillmentOrdersGridActionServiceClearGridState() :void {
    this.fulfillmentOrdersGridActionService.clearGridStateTrigger$
      .pipe(takeUntil(this.onDestroy$))
      .subscribe(() => this.clearGridState());
  }

  private initColumnDefs() :void {
    // context here is used to pass any data/function to the column definition
    const columnDefContext :IFulfillmentOrdersGridColumnDefContext = {
      dropdownOptions: this.fulfillmentOrdersGridActionService.getDropdownActions(),
      ftPushUnPushToFulfilllmentChannel: this.featureToggleService.isActive(
        'red_DV-3334_push_unpush_fulfillment_channel',
      ),
    };
    this.columnDefs = FulfillmentOrdersGridConfig.getColumnConfig(columnDefContext);
  }

  private initClearGrid() :void {
    if (this.clearGridTrigger) {
      this.clearGridTrigger.pipe(takeUntil(this.onDestroy$)).subscribe(() => {
        this.clearGridState();
      });
    }
  }

  private initRefetchGridData() :void {
    if (this.refetchGridDataTrigger) {
      this.refetchGridDataTrigger.pipe(takeUntil(this.onDestroy$)).subscribe(() => {
        this.refetchGridData();
      });
    }
  }

  private initGridOptions() :void {
    // context here is used to pass any data/function to the grid options
    const gridOptionsContext :IFulfillmentOrdersGridOptionsContext = {
      saveGridState: () => {
        AgGridStateUtils.saveAgGridState(
          this.gridOptions,
          this.columnDefs,
          LocalStorageKeys.FULFILLMENT_ORDERS_AG_STATE,
        );
      },
      refetchGridData: () => this.fulfillmentOrdersGridActionService.refreshGrid(),
      updateSelectedRows: () => {
        this.updateSelectedRows();
      },
      fetchFulfillmentOrderItems: (fulfillmentOrderId :number) => {
        let requestParams = {};
        requestParams = AgGridUtils.addFiltersToRequestParams(requestParams, [
          {
            column: 'salesOrderShipmentId',
            operator: 'eq',
            value: fulfillmentOrderId.toString(),
          },
        ]);

        return this.fulfillmentOrdersApiService.getAll({
          relations: fulfillmentOrderDetailRowRelations?.join('|'),
          ...requestParams,
        });
      },
      ftPushUnPushToFulfilllmentChannel: this.featureToggleService.isActive(
        'red_DV-3334_push_unpush_fulfillment_channel',
      ),
    };
    this.gridOptions =
      FulfillmentOrdersGridConfig.getGridOptions(gridOptionsContext);
  }

  private updateSelectedRows() :void {
    const selectedIds :number[] = [];
    const fulfillmentConfirmationValues :any[] = [];
    const fulfillmentOrders: number[] = [];

    this.gridApi.forEachNode((node) => {
      if (node.isSelected() && node.data?.id) {
        selectedIds.push(node.data.id);
        fulfillmentConfirmationValues.push({
          orderId: node.data.id || '',
          fulfillmentChannelName: node.data?.fulfillmentChannel || '',
          warehouseGroupName: node.data?.fulfillmentWarehouseGroup || '',
          fulfillmentChannelStoreName: node.data?.fulfillmentChannelStore || '',
          isPushed: node.data?.pushedAt ? true : false,
          sourceReferenceIdentifier: node.data?.sourceReferenceIdentifier || null,
        });
        fulfillmentOrders.push(node.data);
      }
    });
    this.selectedRowCount$.next(selectedIds.length);
    this.selectedRowIds$.next(selectedIds);

    this.selectedRowCountEvent.emit(selectedIds.length);
    this.selectedRowIdsEvent.emit(selectedIds);
    this.selectedRowsEvent.emit(fulfillmentOrders);
    this.selectedfulfillmentConfirmationValuesEvent.emit(
      fulfillmentConfirmationValues,
    );
  }

  private initDetailRowData() :void {
    this.gridOptions.detailCellRendererParams.getDetailRowData = (params) => {
      if (
        params.data.fulfillmentOrderItems &&
        params.data.fulfillmentOrderItems.length > 0
      ) {
        params.successCallback(params.data.fulfillmentOrderItems);

        return;
      }

      let requestParams = {};
      requestParams = AgGridUtils.addFiltersToRequestParams(requestParams, [
        {
          column: 'salesOrderShipmentId',
          operator: 'eq',
          value: params.data.id.toString(),
        },
      ]);

      this.fulfillmentOrdersApiService
        .getAll({
          relations: fulfillmentOrderDetailRowRelations?.join('|'),
          ...requestParams,
        })
        .pipe(
          takeUntil(this.onDestroy$),
          finalize(() => {
            this.gridOptions.api?.hideOverlay();
          }),
        )
        .subscribe({
          next: (response) => {
            const stockRequests = response.map((item) =>
              this.inventoryService
                .get({
                  sku: item.name,
                  warehouseGroupIds: item.fulfillmentOrder.warehouseGroupId,
                })
                .pipe(takeUntil(this.onDestroy$)),
            );

            forkJoin(stockRequests)
              .pipe(
                takeUntil(this.onDestroy$),
                finalize(() => {
                  this.gridOptions.api?.hideOverlay();
                }),
              )
              .subscribe({
                next: (stockResponses) => {
                  response.forEach((item, idx) => {
                    const stockData = stockResponses[idx]?.data?.[0];
                    const locationStocks = stockData?.location_stocks || [];

                    let totalOnHand = 0;
                    let totalIncoming = 0;

                    locationStocks.forEach((loc) => {
                      totalOnHand += parseFloat(loc.qty_onhand || '0');
                      totalIncoming += parseFloat(loc.qty_incoming || '0');
                    });

                    item.availability = totalOnHand - totalIncoming;
                  });

                  params.data.fulfillmentOrderItems = response;
                  params.successCallback(response);
                },
                error: () => {
                  this.snackBar.open(
                    'Unable to load inventory stock data.',
                    'Error',
                    {
                      duration: SnackBarConfig.DEFAULT.duration,
                    },
                  );
                },
              });
          },
          error: () => {
            this.snackBar.open('Unable to load fulfillment order items.', 'Fail', {
              duration: SnackBarConfig.DEFAULT.duration,
            });
          },
        });
    };
  }
}
